import { AuthType } from '@thesavingsgroup/auth/AuthType';
import { HttpStatusCode } from '@thesavingsgroup/enums/HttpStatusCode';
import i18n from 'i18next';
import { get as lodashGet, isEmpty, toString } from 'lodash';
import { StateCreator } from 'zustand';

import { UserExistsExceptionCode } from './enums/UserExistsExceptionCode';
import { User } from './types/User';
import { UserState } from './types/UserState';
import { UserStateDependencies } from './types/UserStateDependencies';

export const userStore =
  ({
    useAnalyticStore,
    useApiStore,
    useAttributionStore,
    useLeadChannelStore,
    useParameterStore,
  }: UserStateDependencies): StateCreator<UserState> =>
  (set, get) => ({
    fetchCurrentUserFailed: false,
    isUpdatingUser: false,
    user: null,
    userAuthenticateFailedMessage: null,
    userAuthenticateLocked: false,
    userAuthenticateSuccess: false,
    userCreateFailedMessage: null,
    userCreateSuccess: false,
    userExistsException: null,
    userUpdateFailedMessage: null,
    userUpdateSuccess: false,

    computed: {
      get hasCurrentUser(): boolean {
        return !isEmpty(get().user);
      },
      get userEmailAddress(): string {
        const user = get().user;
        const { primaryEmailAddress = '' } = user || {};

        return primaryEmailAddress;
      },
      get userFirstName(): string {
        const user = get().user;
        const { firstName = '' } = user || {};

        return firstName;
      },
      get userLastName(): string {
        const user = get().user;
        const { lastName = '' } = user || {};

        return lastName;
      },
      get userPhoneNumber(): string {
        const user = get().user;
        const { primaryPhoneNumber = '' } = user || {};

        return primaryPhoneNumber;
      },
      get userTruncatedName(): string {
        const user = get().user;
        const { firstName = '', lastName = '' } = user || {};

        return `${firstName} ${lastName.charAt(0)}.`;
      },
    },

    clearUserUpdate: (): void => {
      set({
        isUpdatingUser: false,
        userUpdateFailedMessage: null,
        userUpdateSuccess: false,
      });
    },
    fetchCurrentUser: (): Promise<void> => {
      const portalHttpRequest = useApiStore.getState().portalApi();

      set({ fetchCurrentUserFailed: false });

      return portalHttpRequest
        .get<never, User>('/2.0/users/current')
        .then((user) => {
          set({ user });
        })
        .catch(() => {
          // current user is being checked for on all routes aside from /sign-out.
          // if the user is unable to be fetched in any case, we reset the user state
          // and token to ensure the user cannot access an authenticated route in the SessionGuard.
          useParameterStore.setState({ token: null });

          set({
            fetchCurrentUserFailed: true,
            user: null,
          });
        });
    },
    setUserFirstName: (value: string): void => {
      set({ user: { ...get().user, firstName: value } });
    },
    setUserLastName: (value: string): void => {
      set({ user: { ...get().user, lastName: value } });
    },
    userAgreeToTerms: (vehicleId: string): Promise<void> => {
      if (!vehicleId) {
        return;
      }

      const portalHttpRequest = useApiStore.getState().portalApi();

      return portalHttpRequest.post('/2.0/accepted-terms-of-use', {
        vehicleId,
      });
    },
    userAuthenticate: (
      primaryPhoneNumber: string,
      pin: string,
      leadChannelCode: string | null | undefined,
      purpose: AuthType,
      autopayNumber?: string,
    ): Promise<void> => {
      set({
        userAuthenticateFailedMessage: null,
        userAuthenticateLocked: false,
        userAuthenticateSuccess: false,
      });

      const portalHttpRequest = useApiStore.getState().portalApi();

      const {
        computed: { attributionsForLeadChannel },
      } = useAttributionStore.getState();

      const { vehicleId } = useParameterStore.getState();
      const {
        sendUserActivatedAccountAnalytics,
        sendUserActivatedApplicationAnalytics,
        sendUserAuthenticatedAnalytics,
      } = useAnalyticStore.getState();

      return portalHttpRequest
        .post<never, { user: User }>('/2.0/session', {
          attributions: attributionsForLeadChannel,
          autopayNumber,
          includeUser: true,
          leadChannelCode,
          pin,
          primaryPhoneNumber,
          purpose,
          vehicleId,
        })
        .then(({ user }) => {
          if (purpose === AuthType.ACCOUNT_ACTIVATION_USING_PIN) {
            sendUserActivatedAccountAnalytics();
          }

          if (purpose === AuthType.APPLICATION_ACTIVATION_USING_PIN) {
            sendUserActivatedApplicationAnalytics();
          }

          if (purpose === AuthType.ACCOUNT_SIGN_IN_USING_PIN) {
            sendUserAuthenticatedAnalytics();
          }

          set({ user, userAuthenticateSuccess: true });
        })
        .catch((error) => {
          set({
            userAuthenticateFailedMessage: lodashGet(
              i18n.t('errors.userAuthenticate.statusMessage'),
              toString(error.response.status),
              i18n.t('errors.userAuthenticate.defaultMessage'),
            ),
            userAuthenticateLocked:
              lodashGet(error, 'response.status') === HttpStatusCode.FORBIDDEN,
          });
        });
    },
    userCreate: (
      firstName: string,
      lastName: string,
      primaryEmailAddress: string,
      primaryPhoneNumber: string,
      pin: string,
    ): Promise<void> => {
      set({
        userCreateFailedMessage: null,
        userCreateSuccess: false,
        userExistsException: null,
      });

      const {
        computed: { isValidLeadChannel },
      } = useLeadChannelStore.getState();

      const { leadChannelCode } = useParameterStore.getState();
      const { sendUserRegisteredAnalytics } = useAnalyticStore.getState();

      const portalHttpRequest = useApiStore.getState().portalApi();

      const {
        computed: { attributionsForLeadChannel },
      } = useAttributionStore.getState();

      return portalHttpRequest
        .post<never, { user: User }>('/2.0/users', {
          attributions: attributionsForLeadChannel,
          firstName,
          lastName,
          ...(isValidLeadChannel && { leadChannelCode }),
          pin,
          primaryEmailAddress,
          primaryPhoneNumber,
        })
        .then(({ user }) => {
          sendUserRegisteredAnalytics();

          set({ user, userCreateSuccess: true });
        })
        .catch((error) => {
          const errorCode = lodashGet(
            error,
            'response.data._embedded.errors.0.errorCode',
            null,
          );

          if (errorCode in UserExistsExceptionCode) {
            set({ userExistsException: errorCode });
          } else {
            set({
              userCreateFailedMessage: lodashGet(
                i18n.t('errors.userCreate.statusMessage'),
                toString(error.response.status),
                i18n.t('errors.userCreate.defaultMessage'),
              ),
            });
          }
        });
    },
    userExists: (
      primaryPhoneNumber: string,
      primaryEmailAddress: string,
    ): Promise<void> => {
      set({ userExistsException: null });

      const portalHttpRequest = useApiStore.getState().portalApi();

      return portalHttpRequest
        .get(`/2.0/users`, {
          params: {
            primaryEmailAddress,
            primaryPhoneNumber,
          },
        })
        .catch((error: unknown) => {
          const errorCode = lodashGet(
            error,
            'response.data._embedded.errors.0.errorCode',
            null,
          );

          set({ userExistsException: errorCode });
        });
    },
    userUpdate: (): Promise<void> => {
      const {
        computed: { userFirstName: firstName, userLastName: lastName },
      } = get();

      const portalHttpRequest = useApiStore.getState().portalApi();

      set({
        isUpdatingUser: true,
        userUpdateFailedMessage: null,
        userUpdateSuccess: false,
      });

      return portalHttpRequest
        .patch<never, User>('/2.0/users/current', {
          firstName,
          lastName,
        })
        .then((user) => {
          set({ user, userUpdateSuccess: true });
        })
        .catch((error: unknown) => {
          const errorCode = lodashGet(
            error,
            'response.data._embedded.errors.0.errorCode',
            null,
          );

          if (errorCode in UserExistsExceptionCode) {
            set({ userExistsException: errorCode });
          } else {
            set({
              userUpdateFailedMessage: i18n.t(
                'errors.userUpdate.defaultMessage',
              ),
            });
          }
        })
        .finally(() => {
          set({ isUpdatingUser: false });
        });
    },
  });
